import {
    Button,
    Card,
    CardContent,
    CardHeader,
    colors,
    Grid,
    InputAdornment,
    makeStyles,
    TextField,
    Theme
} from '@material-ui/core';
import {useSnackbar} from 'notistack';
import React, {useEffect, useState} from 'react';
import validate from 'validate.js';
import {Can} from '../../../components/Can';
import ConfirmDelete from '../../../components/ConfirmDelete';
import {LoadingOverlay} from '../../../components/LoadingOverlay/LoadingOverlay';
import anyLoading from '../../../utils/anyLoading';
import {getHandleFieldChange, getHasError} from '../../../utils/formHelpers';
import clsx from 'clsx';
import {red} from '@material-ui/core/colors';
import {AdjustmentTypes} from '../../../graphql/groups.datatypes';
import {BillingParty, BillingPartyAdjustment} from '../../../graphql/billing_party.datatypes';
import updateBillingParty, {UpdateBillingPartyInput} from '../BillingPartyDetails/updateBillingParty.api';

const useStyles = makeStyles((theme: Theme) => ({
    container: {
        marginTop: theme.spacing(3),
        width: '100%',
        marginBottom: theme.spacing(2),
    },
    card: {},
    saveButton: {
        marginLeft: theme.spacing(1),
        color: theme.palette.white,
        backgroundColor: colors.green[600],
        '&:hover': {
            backgroundColor: colors.green[900]
        }
    },
    deleteButton: {
        marginLeft: theme.spacing(1),
        color: theme.palette.white,
        backgroundColor: theme.palette.error.light,
        '&:hover': {
            backgroundColor: theme.palette.error.main
        }
    },
    cloneButton: {
        marginLeft: theme.spacing(1),
        color: theme.palette.white,
        backgroundColor: colors.green[400],
        '&:hover': {
            backgroundColor: colors.green[700]
        }
    },
    errorIcon: {
        marginRight: theme.spacing(1),
        color: theme.palette.error.main,
    },
    marginBottom: {
        paddingLeft: theme.spacing(1),
        marginBottom: theme.spacing(3),
    },
    tabs: {
        marginTop: theme.spacing(3)
    },
    textField: {
        width: '100%',
    },
    cancelled: {
        borderRadius: 5,
        backgroundColor: red[100],
        '&:hover': {
            backgroundColor: red[200]
        }
    },
    chip: {
        backgroundColor: red[200]
    },
}));


export const BillingPartyAdjustmentEdit = (props: {
    billingParty: BillingParty,
    adjustment: BillingPartyAdjustment,
    setSelectedAdjustment?: (adjustment: BillingPartyAdjustment | null) => void,
    className?: string,
    isAdmin: boolean,
    canEdit: boolean,
}) => {
    const classes = useStyles();
    const {billingParty, adjustment, setSelectedAdjustment, className, canEdit, isAdmin} = props;
    const isNew = adjustment.id === -1;
    const [open, setOpen] = React.useState(false);
    const [sendUpdateBillingParty, updateData] = updateBillingParty();
    const {enqueueSnackbar} = useSnackbar();

    //1. Set form and error state
    const [formState, setFormState] = useState({
        ...adjustment,
        description: adjustment.description ? adjustment.description : '',
    });
    const loadingData = anyLoading(updateData);
    const [errors, setErrors] = useState({
        isValid: false,
        errors: {},
    });

    useEffect(() => {
        setFormState({
            ...adjustment,
            description: adjustment.description ? adjustment.description : '',
        });
    }, [props.adjustment])

    //2. Setup helper functions for form
    const handleFieldChange = getHandleFieldChange(setFormState);
    const hasError = getHasError(errors);

    //3. setup validation and error hook
    const validateSchema: any = {
        description: {
            presence: {allowEmpty: false, message: 'is required'},
        },
        amount: {
            numericality: {
                message: 'is required'
            }
        },
    };

    useEffect(() => {
        const newErrors = validate(formState, validateSchema);
        const isValid = !newErrors;

        setErrors(state => ({
            ...state,
            isValid,
            errors: newErrors || {}
        }));
    }, [formState]);

    //4. Handle save form
    const onSaveClick = () => {
        if (isNew) {
            const sanitisedData: UpdateBillingPartyInput = {
                data: {
                    id: billingParty.id,
                    adjustments: {
                        create: [{
                            amount: formState.amount!,
                            adjustment_type: formState.adjustment_type!,
                            description: formState.description!,
                        }],
                    }
                }
            };

            sendUpdateBillingParty({variables: sanitisedData})
                .then(() => {
                    enqueueSnackbar('Adjustment Saved.', {variant: 'success'});
                    if (setSelectedAdjustment) setSelectedAdjustment(null);
                })
                .catch(() => {
                    enqueueSnackbar('Save Adjustment Failed!', {variant: 'error'});
                });
        } else {
            const sanitisedData: UpdateBillingPartyInput = {
                data: {
                    id: billingParty.id,
                    adjustments: {
                        update: [{
                            id: formState.id,
                            amount: formState.amount!,
                            adjustment_type: formState.adjustment_type!,
                            description: formState.description!,
                        }],
                    }
                }
            };

            sendUpdateBillingParty({variables: sanitisedData})
                .then(() => {
                    enqueueSnackbar('Adjustment Saved.', {variant: 'success'});
                    if (setSelectedAdjustment) setSelectedAdjustment(null);
                })
                .catch(() => {
                    enqueueSnackbar('Save Adjustment Failed!', {variant: 'error'});
                });
        }
    };

    const handleClickOpen = () => {
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
    };

    const handleDelete = () => {
        if (!isNew) {
            sendUpdateBillingParty({
                variables: {
                    data: {
                        id: billingParty.id,
                        adjustments: {
                            delete: [formState.id]
                        }
                    }
                }
            })
                .then(() => {
                    enqueueSnackbar('Adjustment Deleted.', {variant: 'success'});
                    if (setSelectedAdjustment) setSelectedAdjustment(null);
                })
                .catch(() => {
                    enqueueSnackbar('Delete Adjustment Failed!', {variant: 'error'});
                });

            handleClose();
        }
    };

    return (

        <Card className={clsx([className, classes.container])}>
            <CardHeader title="Edit Adjustment"/>

            <form>
                <LoadingOverlay loading={loadingData.loading}>
                    <CardContent className={classes.card}>
                        <Grid container spacing={3}>

                            <Grid item xs={12} style={{display: 'flex'}}>
                                <Grid container spacing={3}>

                                    <Grid item xs={6}>
                                        <TextField
                                            fullWidth
                                            type={'number'}
                                            label="amount"
                                            name="amount"
                                            variant="outlined"
                                            onChange={handleFieldChange}
                                            onFocus={event => event.target.select()}
                                            {...hasError('amount')}
                                            value={formState.amount}
                                            InputProps={{
                                                inputProps: {
                                                    onClick: (e) => e.currentTarget.select(),
                                                },
                                                startAdornment: <InputAdornment position="start">$</InputAdornment>,
                                            }}
                                            disabled={!canEdit}
                                        />
                                    </Grid>

                                    <Grid item xs={6}>
                                        <TextField
                                            {...hasError('adjustment_type')}
                                            fullWidth
                                            label="Adjustment Type"
                                            name="adjustment_type"
                                            onChange={handleFieldChange}
                                            select
                                            // eslint-disable-next-line react/jsx-sort-props
                                            SelectProps={{native: true}}
                                            value={formState.adjustment_type}
                                            variant="outlined"
                                            InputLabelProps={{
                                                shrink: true,
                                            }}
                                            disabled={!canEdit}
                                        >
                                            {AdjustmentTypes.map((type, i) => (
                                                <option key={type.name} value={i}>
                                                    {type.name}
                                                </option>
                                            ))}
                                        </TextField>
                                    </Grid>

                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            label="Description"
                                            name="description"
                                            variant="outlined"
                                            onChange={handleFieldChange}
                                            {...hasError('description')}
                                            value={formState.description}
                                            disabled={!canEdit}
                                        />
                                    </Grid>
                                </Grid>
                            </Grid>

                            <Grid item xs={12}>
                                <Button className={classes.saveButton} onClick={onSaveClick} variant="contained"
                                        disabled={!errors.isValid || !canEdit}>Save</Button>

                                {isAdmin && (
                                    <Can permission="groups.update">
                                        <Button onClick={handleClickOpen} className={classes.deleteButton}
                                                variant="contained"
                                                disabled={isNew}>
                                            Delete
                                        </Button>
                                    </Can>
                                )}

                                {setSelectedAdjustment && (
                                    <Button variant={'outlined'}
                                            onClick={() => setSelectedAdjustment(null)}>Close</Button>
                                )}
                            </Grid>

                        </Grid>
                    </CardContent>
                </LoadingOverlay>

                <ConfirmDelete onClose={handleClose} onDeleteClick={handleDelete} open={open}/>

            </form>
        </Card>

    );
};

export default BillingPartyAdjustmentEdit;
